import { takeEvery, takeLatest, put, call, select } from 'redux-saga/effects';
import { cloneDeep, invert } from 'lodash';
import { message } from 'antd';
import {
  getLegacySensitivity,
  saveLegacySensitivityDataForMetric,
  getSensitivityLogs,
} from '@api/aiAlerts';
import { aiAlertMapping, invertProductMap } from '@utils/aiAlerts/metrics';
import { autoRequest } from '@api/createRequestStore';
import * as types from './types';
import * as actions from './actions';
import { sensitivityDataSelector } from './selectors';
import { getMetricDisplayName } from '@src/utils/metrics';

const SENSITIVITY_METRIC_MAP = {
  // key is the metrics defined in the IF client
  // value is the metrics stored in the portal DB
  // ae stands for app
  ae: {
    APP_PAGE_LOAD_TIME: 'AIALERTPAGELOADTIME',
    APP_SCREEN_LOAD_TIME: 'AIALERTSCREENLOADTIME',
    APP_NETWORK_REQUEST_TIME: 'AIALERTNETWORKREQUESTTIME',
    APP_ACTIVE_DEVICES: 'AIALERTACTIVEDEVICES',
  },
  ei: {},
  ai: {},
};

function* initRequest() {
  yield put(actions.init());
}

function* fetchSensitivityData() {
  yield put(actions.setFetching(true));
  try {
    const response = yield autoRequest(getLegacySensitivity, types.SCOPE);

    (response || []).forEach((item) => {
      // update metric add APP prefix
      const metricMap = SENSITIVITY_METRIC_MAP[item.app] || {};
      const invertedMap = invert(metricMap);
      item.metric = invertedMap[item.metric] || item.metric;
    });

    yield put(actions.setSensitivityData(response));
    yield put(actions.setFetching(false));
  } catch (error) {
    const errorMsg = error.message || 'Unknown error!';
    message.error(errorMsg);
    yield put(actions.setError(true));
    yield put(actions.setFetching(false));
  }
}

function* saveSensitivityData(action) {
  yield put(actions.setFetching(true));
  try {
    const { payload, closePopup } = action;

    const apiPayload = cloneDeep(payload);
    delete apiPayload.customerId;
    delete apiPayload.updatedById;
    delete apiPayload.updatedAt;
    delete apiPayload.id;

    if (apiPayload.app === 'ae' && !['APP_ACTIVE_DEVICES'].includes(apiPayload.metric)) {
      apiPayload.sustainPcpDrop = 0;
    }
    const metricMap = SENSITIVITY_METRIC_MAP[apiPayload.app] || {};
    apiPayload.metric = metricMap[apiPayload.metric] || apiPayload.metric;

    const response = yield call(saveLegacySensitivityDataForMetric, { sensitivityObj: apiPayload });
    const sensitivityData = response.data;

    const invertedMap = invert(metricMap);
    sensitivityData.metric = invertedMap[sensitivityData.metric] || sensitivityData.metric;

    let sensitivityConfigList = yield select(sensitivityDataSelector);
    sensitivityConfigList = sensitivityConfigList.filter(
      (item) => item.metric !== sensitivityData.metric,
    );
    sensitivityConfigList.push(sensitivityData);

    yield put(actions.setSensitivityData(sensitivityConfigList));
    yield put(actions.setFetching(false));
    closePopup();

    yield put(actions.setError({ errorMsg: '', errorCode: 0 }));
  } catch (error) {
    const errorMsg = error.message || 'Unknown error!';
    message.error(errorMsg);
    yield put(actions.setError(true));
    yield put(actions.setFetching(false));
  }
}

function* fetchSensitivityLogs() {
  try {
    yield put(actions.setSensitivityLogsFetching(true));
    const response = yield autoRequest(getSensitivityLogs, types.SCOPE);
    // format app log
    (response || []).map((log) => {
      const metricMap = SENSITIVITY_METRIC_MAP[log.app] || {};
      const invertedMap = invert(metricMap);
      log.metric = invertedMap[log.metric] || log.metric;
      const metricKey = aiAlertMapping[log.metric] || log.metric;
      const context = invertProductMap[log.app] || log.app;
      log.metricKey = metricKey;
      log.metric = getMetricDisplayName({ metricKey, context });
      return log;
    });
    yield put(actions.setSensitivityLogs(response));
    yield put(actions.setSensitivityLogsFetching(false));
  } catch (error) {
    const errorMsg = error.message || 'Unknown error!';
    message.error(errorMsg);
    yield put(actions.setLogsError(true));
    yield actions.setSensitivityLogsFetching(false);
  }
}

function* watchInitRequest() {
  yield takeLatest([types.INIT], initRequest);
}

function* watchParamChanges() {
  yield takeEvery([types.FETCH_DATA], fetchSensitivityData);
}

function* watchFetchingLogs() {
  yield takeLatest([types.FETCH_SENSITIVITY_LOGS], fetchSensitivityLogs);
}

function* watchSaveData() {
  yield takeLatest(types.SAVE_SENSITIVITY_DATA, saveSensitivityData);
}

export default [watchInitRequest, watchParamChanges, watchSaveData, watchFetchingLogs];
